import { createSlice } from "@reduxjs/toolkit";
import { Status } from "lib/enums";
import { formatMainDataDates } from "lib/helpers";
import { SiteType } from "lib/types";
import { RootState } from "reducers/store";
import { getSitesThunk as getSites, postSiteThunk as postSite, updateSiteThunk as updateSite, deleteSiteThunk as deleteSite, duplicateSite } from "./action";

export type SitesState = {
    data: { [id: number]: SiteType[] },
    sitesStatus: { [id: number]: Status },
    backendErrors: {
        siteStatusErrorMsg: string;
    };
    upsertStatus: Status
};

const initialState: SitesState = {
    data: {},
    sitesStatus: {},
    backendErrors: {
        siteStatusErrorMsg: "string",
    },
    upsertStatus: Status.Idle
};

const getSitesSlice = createSlice({
    name: "sites",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSites.pending, (state, action) => {
                state.sitesStatus[action.meta.arg] = Status.Pending;
            })
            .addCase(getSites.rejected, (state, action) => {
                state.sitesStatus[action.meta.arg] = Status.Rejected;
                state.backendErrors.siteStatusErrorMsg = action.payload;
            })
            .addCase(getSites.fulfilled, (state, action) => {
                state.sitesStatus[action.meta.arg] = Status.Success;
                state.data[action.meta.arg] = formatMainDataDates(action.payload.data.sites.sort((a, b) => b.id - a.id));
                state.backendErrors.siteStatusErrorMsg = "";
            })
            .addCase(postSite.rejected, (state, action) => {
                state.backendErrors.siteStatusErrorMsg = action.payload;
            })
            .addCase(postSite.pending, (state, action) => {
                state.upsertStatus = Status.Pending
            })
            .addCase(postSite.fulfilled, (state, action) => {
                state.backendErrors.siteStatusErrorMsg = "";
                state.upsertStatus = Status.Success
            }).addCase(deleteSite.rejected, (state, action) => {
                state.backendErrors.siteStatusErrorMsg = action.payload;
            }).addCase(deleteSite.fulfilled, (state, action) => {
                state.backendErrors.siteStatusErrorMsg = "";
                state.upsertStatus = Status.Success
            })
            .addCase(updateSite.rejected, (state, action) => {
                state.backendErrors.siteStatusErrorMsg = action.payload;
            })
            .addCase(updateSite.pending, (state, action) => {
                state.upsertStatus = Status.Pending
            })
            .addCase(updateSite.fulfilled, (state, action) => {
                state.backendErrors.siteStatusErrorMsg = "";
                state.upsertStatus = Status.Success
            })
            .addCase(duplicateSite.rejected, (state, _) => {
                state.upsertStatus = Status.Rejected
            })
            .addCase(duplicateSite.pending, (state, _) => {
                state.upsertStatus = Status.Pending
            })
            .addCase(duplicateSite.fulfilled, (state, action) => {
                state.upsertStatus = Status.Success
            });
    },
});

export const selectSites = (projectId: string) => (state: RootState): SiteType[] => state.sites.data[projectId] ? state.sites.data[projectId] : []
export const selectStatus = (projectId: string) => (state: RootState): Status => state.sites.sitesStatus[projectId] ? state.sites.sitesStatus[projectId] : Status.Idle
export const selectAreSitesLoading = (state: RootState): boolean => Object.values(state.sites.sitesStatus).some(status => status === Status.Pending) || state.sites.upsertStatus === Status.Pending

export default getSitesSlice.reducer;